import type {
  SupportReportContext,
  SupportTicketType,
} from '@support/entities/support-ticket';
import { useAuthData } from '@auth/features/auth';
import {
  detectPageContext,
  useCreateTicket,
  useResolveItineraryRef,
  useSupportDrawerStore,
} from '@support/entities/support-ticket';
import { SimilarTickets } from '@support/features/ticket-similar';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  Select,
  Text,
  Textarea,
  toastStore,
} from '@/shared/ui';
import { useTicketAttachments } from '../model/use-ticket-attachments';
import { AttachmentsField } from './attachments-field';
import css from './create-support-ticket-modal.module.scss';
import { Required, TYPE_OPTIONS } from './form-parts';

// The itinerary the reporter is on, if any — sent so the server can link the
// ticket to it. Planner routes (`/itineraries/:id`) surface it as
// `itineraryId`; Traveller Preview (`/preview/:id`) carries the SAME itinerary
// uuid under `entityType: 'preview'`, so both count. Any other page yields
// undefined and the ticket is filed without an itinerary.
const detectItineraryId = (ctx: SupportReportContext): string | undefined => {
  if (ctx.itineraryId) return ctx.itineraryId;
  if (ctx.entityType === 'preview' && ctx.entityId) return ctx.entityId;
  return undefined;
};

// Global "create support ticket" modal — mounted once, driven by the shared
// support-drawer store (same pattern as incidents' ReportIncidentModal). A
// live similar-tickets search renders under the Title field so the reporter
// can upvote an existing ticket instead of filing a duplicate; that list is
// ADVISORY ONLY and never gates submission (see canSubmit below).
export const CreateSupportTicketModal = () => {
  const reportOpen = useSupportDrawerStore((s) => s.reportOpen);
  const closeReport = useSupportDrawerStore((s) => s.closeReport);
  const createTicket = useCreateTicket();
  // `isPending` only flips on the next render, so a fast double-click would
  // otherwise file the same ticket twice.
  const submittingRef = useRef(false);
  const { data: authData } = useAuthData();
  // File storage (UploadThing) may be unconfigured on a deployment — hide the
  // attachment affordance rather than let the user hit a 503.
  const uploadsEnabled = authData?.uploadsEnabled ?? false;

  const [context, setContext] = useState<SupportReportContext>({});
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const {
    attachments,
    setAttachments,
    isUploading,
    uploadValidFiles,
    removeAttachment,
    onPaste,
  } = useTicketAttachments(uploadsEnabled);

  // The itinerary this report is about, when the reporter is on a Planner or
  // Preview page. The id is what gets recorded; the reference is fetched only
  // to label the context chip.
  const contextItineraryId = detectItineraryId(context);
  const { data: itineraryRefData } = useResolveItineraryRef(contextItineraryId);
  const itineraryRef = itineraryRefData?.itineraryRef ?? null;

  // Reset + re-capture context each time the modal opens.
  useEffect(() => {
    if (reportOpen) {
      setContext(detectPageContext({ accountId: authData?.user.id }));
      setTitle('');
      setDescription('');
      setType('');
      setAttachments([]);
    }
  }, [reportOpen, authData?.user.id, setAttachments]);

  if (!reportOpen) return null;

  const canSubmit = Boolean(title.trim() && description.trim() && type);

  const handleSubmit = () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    createTicket.mutate(
      {
        title: title.trim(),
        description: description.trim(),
        type: type as SupportTicketType,
        attachments,
        applicationUrl: context.href ?? window.location.href,
        sourceModule: context.module,
        pageContext: context,
        entityType: context.entityType,
        entityId: context.entityId,
        itineraryId: contextItineraryId,
      },
      {
        onSuccess: () => {
          toastStore.getState().show({ message: 'Support ticket created' });
          closeReport();
        },
        onError: () =>
          toastStore
            .getState()
            .show({ message: 'Could not create the ticket' }),
        onSettled: () => {
          submittingRef.current = false;
        },
      }
    );
  };

  return (
    <Modal
      onClose={closeReport}
      // The Support drawer underneath also closes on Escape (document
      // listener); stopping it here keeps the drawer and closes only the form.
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        closeReport();
      }}
      open={reportOpen}
    >
      <ModalHeader onClose={closeReport} title="Create support ticket" />
      {/* `onPaste` here (not on the Textarea) so a clipboard screenshot
          becomes an attachment wherever the caret is inside the modal. */}
      <div className={css.form} onPaste={onPaste}>
        {/* Origin — auto-detected page the report came from (best-effort
            audit trail; also sent as sourceModule/pageContext on submit).
            When that page is an itinerary (Planner or Preview) its reference
            is shown too, so the reporter can see it is being recorded. */}
        <div className={css.origin}>
          <Text as="span" size="xs" variant="muted">
            Reporting from
          </Text>
          <Badge variant="booked">{context.module ?? 'Xthory'}</Badge>
          {itineraryRef ? (
            <Badge variant="pending">{itineraryRef}</Badge>
          ) : null}
        </div>

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Title <Required />
          </Text>
          <Input
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short summary of the issue"
            value={title}
          />
        </div>

        {/* Live similar-ticket suggestions — advisory only, see module doc. */}
        <SimilarTickets query={title} />

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Description <Required />
          </Text>
          <Textarea
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened? Steps to reproduce, expected vs actual, and impact."
            rows={4}
            value={description}
          />
        </div>

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Type <Required />
          </Text>
          <Select
            aria-label="Type"
            onChange={(e) => setType(e.target.value)}
            options={TYPE_OPTIONS}
            placeholder="Select a type"
            usePortal
            value={type}
          />
        </div>

        {/* Attachments — always visible (gated only by uploadsEnabled, never
            by a feature flag). */}
        {uploadsEnabled ? (
          <AttachmentsField
            attachments={attachments}
            isUploading={isUploading}
            onFiles={uploadValidFiles}
            onRemove={removeAttachment}
          />
        ) : null}
      </div>
      <ModalFooter>
        <Button onClick={closeReport} variant="ghost">
          Cancel
        </Button>
        <Button
          disabled={!canSubmit || createTicket.isPending || isUploading}
          onClick={handleSubmit}
          variant="primary"
        >
          {createTicket.isPending ? 'Creating…' : 'Create ticket'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
